import type {
  Address,
  Appointment,
  AppointmentTechnician,
  CustomerDocument,
  CustomerNote,
  CustomerPhoto,
} from '../../../stores/useAppointmentsStore';
import { PALETTE } from '../../../realtime/techColors';

/**
 * Authoritative wire shape returned by `/v1/appointments/:id`.
 *
 * Source of truth:
 *   - /Users/chris/Sera/api/app/views/v1/appointments/show.json.jbuilder
 *   - /Users/chris/Sera/api/app/views/v1/appointments/_appointment.json.jbuilder
 *   - /Users/chris/Sera/api/app/views/v2/addresses/_address.json.jbuilder
 *
 * The Vue 3 `Appointment` interface was authored against a presumed shape that
 * did not match the jbuilder output. This adapter is the seam where wire
 * divergence is absorbed:
 *
 *   - wire `iso_date_time`     → interface `scheduled_time`
 *   - wire `iso_arrival_time`  → interface `arrival_time`
 *   - wire `iso_departure_time`→ interface `departure_time`
 *   - wire `address.street`    → interface `address.address1`
 *   - wire `address.unit`      → interface `address.address2`
 *   - wire `photos[].file`     → interface `photos[].url`
 *   - wire `technician.{first_name,last_name}` → interface `technician.name`
 *   - wire `cancellation_details` (jsonb, `{}` default or `{ reason, responsable }`)
 *       → interface `cancellation: { reason, party }` (responsable maps to party)
 *   - wire `settings` is preserved (consumer needs it for the late fee timer)
 *
 * Without this adapter, every section of AppointmentSummaryView and
 * AppointmentDetailView reads undefined values; `formatInTZ(undefined, ...)`
 * raises a RangeError that Vue silently swallows at the binding boundary and
 * the page renders fully blank despite a 200 OK response.
 */
export interface AppointmentWireAddress {
  id?: number;
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  time_zone?: string;
  primary?: boolean;
  name?: string;
  invalid?: boolean;
  /** Geocoded coordinates from `address.coords` (jbuilder); null when the
   * address has not been geocoded. Drives the tracking-map center (TRACK-01). */
  latitude?: number | null;
  longitude?: number | null;
}

export interface AppointmentWireTechnician {
  id?: number;
  technician_profile_id?: number;
  /**
   * The technician's USER id, when the singular wire carries it. Back-filled
   * onto the adapted tech so the live map's `colorByUserId` bridge can match a
   * GPS frame (keyed by user id) to this tech's roster color (HI-02).
   */
  user_id?: number;
  first_name?: string;
  last_name?: string;
  // 260524-lo7 (G6): v1 jbuilder emits technician.phone for appointment-scope
  // auth (the legacy consumer-portal Contact Tech menu reads it). Consumed by
  // the magic-link Contact Tech pill (ContactTechCard.vue).
  phone?: string;
}

/**
 * Per-tech entry on the v2 `technicians[]` (and v1
 * `appointment_technicians[]`) arrays. Wider than the singular legacy
 * `AppointmentWireTechnician` because the per-tech row carries its own
 * lifecycle + ETA / distance / display fields. All fields are optional —
 * the adapter back-fills with safe defaults so a partially-populated row
 * still renders a usable card.
 */
export interface AppointmentWireMultiTech {
  id?: number;
  /**
   * Legacy v1 appointment_technicians[] entry uses `technician_id` instead
   * of `id`. Adapter falls back to this when `id` is absent.
   */
  technician_id?: number;
  /**
   * The technician's USER id (`technician_profile.user_id`), emitted by the
   * `v2/technicians/_technician` partial. Carried through so the live map can
   * reconcile a GPS frame's `technicianId` (= user id) to the right tech.
   */
  user_id?: number;
  name?: string;
  /**
   * Legacy v1 appointment_technicians[] entry uses `full_name` instead of
   * `name`. Adapter falls back to this when `name` / `first_name` are both
   * absent.
   */
  full_name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  /**
   * Skill level emitted by the v1 wire — "master", "senior", "journeyman",
   * "apprentice", etc. When present, the adapter prepends the title-cased
   * value to the department name so the per-tech row reads as
   * "Master · Plumbing" rather than just "Plumbing".
   */
  skill_level?: string;
  /**
   * Legacy v1 nests the department under `department.name`. When `role` is
   * absent, the adapter back-fills from the department name (e.g.
   * "Plumbing", "HVAC"), optionally prefixed with `skill_level`.
   */
  department?: { name?: string } | null;
  avatar_url?: string;
  /**
   * Legacy v1 nests avatar as `avatar: { url }`. Adapter falls back to
   * `avatar.url` when `avatar_url` is absent.
   */
  avatar?: { url?: string | null } | null;
  phone?: string;
  technician_status?: string;
  arrived_at?: string;
  departed_at?: string;
  /**
   * v1 `appointment_technicians[]` join-row fields. Status + on-site
   * timestamps live ONLY on the join array (the `technicians[]` profile
   * partial omits them), so the adapter merges these onto the matching
   * profile row keyed by `technician_profile_id` ↔ `technician_id`.
   */
  technician_profile_id?: number;
  arrival_time?: string;
  departure_time?: string;
  eta_minutes?: number;
  distance_miles?: number;
  route_color?: string;
}

export interface AppointmentWireNote {
  id: number;
  comment?: string;
  customer_visible?: boolean;
  pinned?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * v1/photos/_photo.json.jbuilder emits `file` as the serialized CarrierWave
 * uploader, which marshals to `{ url, thumb: { url } }`. Earlier tests
 * (and a handful of mocks) modeled it as a bare URL string — preserved as a
 * union so the adapter accepts both shapes gracefully.
 */
export type AppointmentWireFile =
  | string
  | { url?: string | null; thumb?: { url?: string | null } | null };

export interface AppointmentWirePhoto {
  id: number;
  job_id?: number;
  file: AppointmentWireFile;
  source?: string;
  created_at?: string;
  created_by_name?: string;
  customer_visible: boolean;
  // Free-text media caption (PODZ-1830). Assumed wire field name `caption`;
  // single change point if PODZ-1823 ships a different name. Absent until
  // that backend deploys.
  caption?: string;
}

export interface AppointmentWireDocument {
  id: number;
  job_id?: number;
  name?: string;
  url?: string;
  download_url?: string;
  source?: string;
  created_by_name?: string;
  customer_visible: boolean;
  created_at?: string;
  thumbnail?: { url?: string | null } | null;
}

export interface AppointmentWireSettings {
  late_fee_per_minute_cents?: number;
  max_late_fee_cents?: number;
}

export interface AppointmentWire {
  id: number;
  public_id?: string;
  job_id: number;
  job_public_id?: string;
  // The owning customer. Customer-visible notes are polymorphic Comments on
  // the User (commentable_type=User), so the detail/magic-link views need the
  // customer id to fetch them. `cable_token` is the customer's AnyCable bearer
  // token — the api serializes it on the magic-link appointment_viewer scope so
  // the tracking map can connect live tracking on the shared link (PODZ-1821).
  customer?: { id: number; cable_token?: string } | null;
  status: string;
  iso_date_time?: string | null;
  iso_arrival_time?: string | null;
  iso_departure_time?: string | null;
  arrival_window_start?: string | null;
  arrival_window_end?: string | null;
  /**
   * v2 dispatch timestamp (Phase 4.6). Supersedes the v1 typo
   * `iso_dispatch_time`; adapter normalises both into a single
   * `Appointment.dispatch_time`.
   */
  dispatch_time?: string | null;
  /** v1 dispatch timestamp (legacy typo + RFC-2822 / ISO mix). */
  iso_dispatch_time?: string | null;
  service_category?: { id: number; name: string } | null;
  address?: AppointmentWireAddress | null;
  technician?: AppointmentWireTechnician | null;
  /**
   * v2 canonical multi-tech array (CONTEXT D-14). Empty array indicates
   * no tech assigned yet — distinct from `undefined` which means the
   * wire didn't ship the field at all (older payload).
   */
  technicians?: AppointmentWireMultiTech[];
  /**
   * v1 join-table-shaped projection of the multi-tech array. Mapped
   * into `technicians[]` when the canonical field is empty/absent.
   */
  appointment_technicians?: AppointmentWireMultiTech[];
  // Postgres JSONB column with default `{}`. Populated rows write
  // `{ reason, responsable }` (per v2/appointments_controller.rb:143). A bare
  // string is preserved as a backward-compatible reading; anything else is
  // ignored. Typed as `unknown` because the runtime shape is wider than what
  // the v1 jbuilder pretends.
  cancellation_details?: unknown;
  photos?: AppointmentWirePhoto[];
  documents?: AppointmentWireDocument[];
  notes?: AppointmentWireNote[];
  settings?: AppointmentWireSettings | null;
  // Wire emits `appointment.ticket.total_real` as a pre-formatted money string
  // (e.g. "$0.00", "$340.00") when the job has an invoice. AppointmentSummaryView's
  // invoiceTotalLabel reads `appointment.invoice_total` — we pass total_real through
  // under that key so a completed $0 invoice renders the formatted total instead
  // of falling through to "Pending".
  ticket?: {
    total_real?: string;
    balance?: string;
    balance_cents?: number;
  } | null;
}

const EMPTY_ADDRESS: Address = {
  id: 0,
  address1: '',
  city: '',
  state: '',
  zip_code: '',
  time_zone: '',
};

function str(value: string | undefined | null, fallback = ''): string {
  return value ?? fallback;
}

function adaptAddress(
  wire: AppointmentWireAddress | null | undefined,
): Address {
  if (!wire) return { ...EMPTY_ADDRESS };
  const unit = wire.unit?.trim() ?? '';
  const base: Address = {
    id: wire.id ?? 0,
    address1: str(wire.street),
    city: str(wire.city),
    state: str(wire.state),
    zip_code: str(wire.zip_code),
    time_zone: str(wire.time_zone),
    name: wire.name,
    invalid: wire.invalid,
    primary: wire.primary,
    latitude: wire.latitude ?? null,
    longitude: wire.longitude ?? null,
  };
  return unit ? { ...base, address2: unit } : base;
}

function adaptTechnician(
  wire: AppointmentWireTechnician | null | undefined,
): Appointment['technician'] {
  if (!wire) return undefined;
  const first = wire.first_name?.trim() ?? '';
  const last = wire.last_name?.trim() ?? '';
  const name = [first, last].filter(Boolean).join(' ');
  if (!name) return undefined;
  // 260524-lo7 (G6): pass technician.phone through when the wire emits it.
  const phone = wire.phone?.trim();
  return phone ? { name, phone } : { name };
}

function adaptCancellation(raw: unknown): Appointment['cancellation'] {
  if (raw === null || raw === undefined) return undefined;

  // Wire actually emits a JSONB object — `{}` for non-cancelled rows (the
  // Postgres column default) or `{ reason, responsable }` for cancelled rows.
  // `responsable` is the email of the cancelling actor and maps to `party`.
  if (typeof raw === 'object') {
    if (Array.isArray(raw)) return undefined;
    const obj = raw as { reason?: unknown; responsable?: unknown };
    const reason = typeof obj.reason === 'string' ? obj.reason.trim() : '';
    if (!reason) return undefined;
    const party =
      typeof obj.responsable === 'string' ? obj.responsable.trim() : '';
    return { reason, party };
  }

  // Defensive backward-compat path: if some endpoint ever flattens
  // cancellation_details to a string, preserve it as `reason`. Never reached
  // with the current v1 wire but cheap and safe.
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    return { reason: trimmed, party: '' };
  }

  return undefined;
}

function adaptPhotoFile(file: AppointmentWireFile): {
  url: string;
  thumbUrl: string;
} {
  // Photo file is normally the CarrierWave-serialized object
  // `{ url, thumb: { url } }`. Older mocks (and a handful of older tests)
  // pass it as a bare string — treat that as the full url with no thumb.
  if (typeof file === 'string') return { url: file, thumbUrl: '' };
  return {
    url: file?.url ?? '',
    thumbUrl: file?.thumb?.url ?? '',
  };
}

/**
 * Map the photo wire array to `CustomerPhoto[]`. Exported so the v2 job
 * adapter (`adaptJob`) can reuse it: the v2 `/v2/jobs/:id` photo shape
 * (v2/photos/_photo.json.jbuilder) is a SUPERSET of `AppointmentWirePhoto`
 * (adds `name`, `download_url`, `links`) and already carries the fields this
 * helper reads (file, source, created_at, created_by_name, customer_visible,
 * caption), so a single mapper serves both wires with no duplication.
 */
export function adaptPhotos(
  wire: AppointmentWirePhoto[] | undefined,
): CustomerPhoto[] {
  if (!wire) return [];
  return wire.map((p) => {
    const { url, thumbUrl } = adaptPhotoFile(p.file);
    const out: CustomerPhoto = {
      id: p.id,
      url,
      // `thumbUrl` falls back to the full url so grid `<img>` always has a
      // src. Real-world data uses the 200x200 thumb; CarrierWave skips the
      // thumb version when `is_invoice_ready?` returns false, in which case
      // the full image stands in.
      thumbUrl: thumbUrl || url,
      customer_visible: p.customer_visible,
      createdAt: p.created_at,
      createdByName: p.created_by_name,
    };
    // Conditional assign — drop absent/empty wire captions so the key is only
    // present for real text (matches adaptJobVideos; PODZ-1830 / D-CAP-DEFENSIVE).
    if (p.caption) out.caption = p.caption;
    // Conditional assign — thread the upload source enum so the upload-meta
    // footer can render a source label (07.1-02 maps it). Absent/empty leaves
    // the key off entirely (D-CAP-SOURCE-MAP defensive omit).
    if (p.source) out.source = p.source;
    return out;
  });
}

function adaptDocuments(
  wire: AppointmentWireDocument[] | undefined,
): CustomerDocument[] {
  if (!wire) return [];
  return wire.map((d) => ({
    id: d.id,
    name: d.name ?? '',
    url: d.url ?? '',
    // download_url applies a Content-Disposition: attachment header so a
    // click sends the file rather than opening it inline (v2 jbuilder).
    // Falls back to plain url when older shapes omit it.
    downloadUrl: d.download_url || d.url || '',
    thumbUrl: d.thumbnail?.url ?? '',
    customer_visible: d.customer_visible,
    createdAt: d.created_at,
    createdByName: d.created_by_name,
  }));
}

function adaptSettings(
  wire: AppointmentWireSettings | null | undefined,
): NonNullable<Appointment['settings']> {
  return {
    late_fee_per_minute_cents: wire?.late_fee_per_minute_cents ?? 0,
    max_late_fee_cents: wire?.max_late_fee_cents ?? 0,
  };
}

/**
 * Deterministic route-color back-fill used when the wire omits a per-tech
 * color (RESEARCH §3). Draws from the single shared 20-color `PALETTE` so the
 * adapter and `resolveColors` never drift — each tech in a realistic crew gets
 * a distinct, stable hue (the modulo only wraps past twenty techs). Because
 * `resolveColors` is backend-first, this back-filled value is what actually
 * drives the polyline + dot color, so it MUST stay distinct per tech.
 */
function pickRouteColor(index: number): string {
  return PALETTE[index % PALETTE.length];
}

const VALID_TECH_STATUSES = new Set([
  'scheduled',
  'assigned',
  'accepted',
  'traveling',
  'arrived',
  'departed',
]);

function normaliseTechStatus(
  raw: string | undefined,
): AppointmentTechnician['technician_status'] {
  const lower = (raw ?? '').toLowerCase();
  if (VALID_TECH_STATUSES.has(lower)) {
    return lower as AppointmentTechnician['technician_status'];
  }
  // Unknown/empty default — keeps downstream pill rendering valid. The full
  // backend enum (assigned/accepted/traveling/arrived/departed) is now
  // recognised; `scheduled` only survives as the no-data fallback.
  return 'scheduled';
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Build the subtitle role string from the v1 wire's `skill_level` +
 * `department.name`. Skill comes first ("Master · Plumbing"). Returns
 * undefined when both inputs are absent so downstream `if (role)` gates
 * stay clean.
 */
function buildRoleFromV1(wire: AppointmentWireMultiTech): string | undefined {
  const skill = wire.skill_level?.trim();
  const dept = wire.department?.name?.trim();
  const parts: string[] = [];
  if (skill) parts.push(titleCase(skill));
  if (dept) parts.push(dept);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function buildTechName(wire: AppointmentWireMultiTech): string {
  // Field priority: flat `name` → legacy `full_name` → joined first+last.
  // Some payloads ship a flat `name`, the v1 appointment_technicians[]
  // ships `full_name`, others split first/last. All three paths return
  // a usable string even when the wire is partially populated.
  if (wire.name && wire.name.trim()) return wire.name.trim();
  if (wire.full_name && wire.full_name.trim()) return wire.full_name.trim();
  const first = wire.first_name?.trim() ?? '';
  const last = wire.last_name?.trim() ?? '';
  return [first, last].filter(Boolean).join(' ');
}

function attachDerivedTechFields(
  tech: AppointmentTechnician,
  wire: AppointmentWireMultiTech,
): void {
  const role = wire.role ?? buildRoleFromV1(wire);
  if (role) tech.role = role;
  const avatarUrl = wire.avatar_url ?? wire.avatar?.url ?? undefined;
  if (avatarUrl) tech.avatar_url = avatarUrl;
}

function attachDirectTechFields(
  tech: AppointmentTechnician,
  wire: AppointmentWireMultiTech,
): void {
  if (wire.phone) tech.phone = wire.phone;
  // v1 join rows emit `arrival_time`/`departure_time`; the portal type uses
  // `arrived_at`/`departed_at`. Accept either shape.
  const arrived = wire.arrived_at ?? wire.arrival_time;
  const departed = wire.departed_at ?? wire.departure_time;
  if (arrived) tech.arrived_at = arrived;
  if (departed) tech.departed_at = departed;
  if (typeof wire.eta_minutes === 'number') tech.eta_minutes = wire.eta_minutes;
  if (typeof wire.distance_miles === 'number') {
    tech.distance_miles = wire.distance_miles;
  }
}

function adaptMultiTech(
  wire: AppointmentWireMultiTech,
  index: number,
): AppointmentTechnician {
  // ID fall-back: legacy appointment_technicians[] uses `technician_id`
  // instead of `id`. The internal type is non-optional so we coerce to 0
  // when neither is present — the row still renders, just without a
  // route-color mapping anchor.
  const id = wire.id ?? wire.technician_id ?? 0;
  const tech: AppointmentTechnician = {
    id,
    user_id: wire.user_id,
    name: buildTechName(wire),
    technician_status: normaliseTechStatus(wire.technician_status),
    route_color: wire.route_color ?? pickRouteColor(index),
  };
  attachDerivedTechFields(tech, wire);
  attachDirectTechFields(tech, wire);
  return tech;
}

/**
 * Wrap the singular `wire.technician` into a one-element multi-tech array
 * — the legacy v1 payload only carried a single tech but downstream UI
 * always reads off `technicians[]`.
 */
function adaptSingularTechAsMulti(
  wire: AppointmentWireTechnician,
): AppointmentTechnician | undefined {
  const first = wire.first_name?.trim() ?? '';
  const last = wire.last_name?.trim() ?? '';
  const name = [first, last].filter(Boolean).join(' ');
  if (!name) return undefined;
  const tech: AppointmentTechnician = {
    id: wire.id ?? 0,
    name,
    technician_status: 'scheduled',
    route_color: pickRouteColor(0),
  };
  // HI-02: back-fill the USER id when the wire carries it so the live color
  // bridge (colorByUserId) reconciles a user-id-keyed GPS frame to this tech.
  if (typeof wire.user_id === 'number') tech.user_id = wire.user_id;
  if (wire.phone) tech.phone = wire.phone;
  return tech;
}

/**
 * Back-fill the canonical multi-tech array (CONTEXT D-14):
 *
 *   1. wire.technicians[] populated  → adapt as-is (v2 canonical)
 *   2. wire.appointment_technicians[] → adapt as-is (v1 join projection)
 *   3. wire.technician (singular)    → wrap in a one-element array
 *   4. nothing                       → omit the field entirely
 *
 * Downstream rendering reads off `technicians[]`; the omitted-field
 * sentinel lets the hospitality card distinguish "no team yet" from
 * "empty team list returned" if needed.
 */
/**
 * Merge the v1 `appointment_technicians[]` join rows onto the `technicians[]`
 * profile rows. The profile partial carries names/avatars but NOT per-tech
 * status or on-site timestamps — those live only on the join array. Match on
 * `technician_profile_id` ↔ `technician_id` (fall back to `id`). Profile
 * values win when present (`??`) so a payload that already carries status
 * on `technicians[]` is left untouched.
 */
function mergeTechJoinRows(
  profiles: AppointmentWireMultiTech[],
  joinRows: AppointmentWireMultiTech[],
): AppointmentWireMultiTech[] {
  const byId = new Map<number, AppointmentWireMultiTech>();
  for (const j of joinRows) {
    const key = j.technician_profile_id ?? j.technician_id ?? j.id;
    if (key != null) byId.set(key, j);
  }
  return profiles.map((p) => {
    const key = p.technician_id ?? p.id;
    const j = key != null ? byId.get(key) : undefined;
    if (!j) return p;
    return {
      ...p,
      technician_status: p.technician_status ?? j.technician_status,
      arrival_time: p.arrival_time ?? j.arrival_time,
      departure_time: p.departure_time ?? j.departure_time,
      arrived_at: p.arrived_at ?? j.arrived_at,
      departed_at: p.departed_at ?? j.departed_at,
    };
  });
}

function adaptTechnicians(
  wire: AppointmentWire,
): AppointmentTechnician[] | undefined {
  const v2 = wire.technicians;
  const v1Join = wire.appointment_technicians;
  if (v2 && v2.length > 0) {
    // The v1 endpoint emits BOTH arrays — profiles (names) + join rows
    // (status/timestamps). Merge so the per-tech card has both.
    const merged =
      v1Join && v1Join.length > 0 ? mergeTechJoinRows(v2, v1Join) : v2;
    return merged.map((t, i) => adaptMultiTech(t, i));
  }
  if (v1Join && v1Join.length > 0)
    return v1Join.map((t, i) => adaptMultiTech(t, i));
  if (!wire.technician) return undefined;
  const single = adaptSingularTechAsMulti(wire.technician);
  return single ? [single] : undefined;
}

function adaptNotes(wire: AppointmentWireNote[] | undefined): CustomerNote[] {
  if (!wire) return [];
  // Research-gated (CONTEXT D-35): keep `customer_visible` undefined out
  // because legacy never surfaced notes on the customer view. Only true
  // gets through; downstream renders nothing when the array is empty.
  return wire
    .filter((n) => n.customer_visible === true)
    .map((n) => {
      const out: CustomerNote = {
        id: n.id,
        comment: n.comment ?? '',
        customer_visible: true,
      };
      if (typeof n.pinned === 'boolean') out.pinned = n.pinned;
      if (n.created_at) out.createdAt = n.created_at;
      if (n.updated_at) out.updatedAt = n.updated_at;
      return out;
    });
}

/**
 * Raw v1 cancellation block — the `responsable` typo is preserved
 * verbatim (CONTEXT D-36 / RESEARCH §1.5). Distinct from the legacy
 * `adaptCancellation()` projection (which renames responsable → party);
 * both shapes ship in parallel so existing consumers keep working while
 * the cancelled-state surfaces read off the typed raw shape.
 */
function adaptCancellationDetails(
  raw: unknown,
): Appointment['cancellation_details'] {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const obj = raw as { reason?: unknown; responsable?: unknown };
  const reason = typeof obj.reason === 'string' ? obj.reason.trim() : '';
  if (!reason) return undefined;
  const responsable =
    typeof obj.responsable === 'string' ? obj.responsable.trim() : '';
  return { reason, responsable };
}

/**
 * Normalise the API's `cancelled` / `canceled` spelling at the wire
 * boundary (CONTEXT D-37). Downstream consumers can rely on
 * `appointment.status === 'cancelled'`; the lifecycle helper accepts
 * both spellings defensively.
 */
function normaliseStatus(raw: string | undefined): string {
  const lower = (raw ?? '').toLowerCase();
  return lower === 'canceled' ? 'cancelled' : lower;
}

/**
 * Attach every optional Appointment field that the wire only sometimes
 * emits. Pulled out of {@link adaptAppointment} so that function stays
 * under the project's cyclomatic-complexity ceiling (10).
 */
function attachOptionalFields(
  target: Appointment,
  wire: AppointmentWire,
): void {
  const technician = adaptTechnician(wire.technician);
  if (technician) target.technician = technician;

  const technicians = adaptTechnicians(wire);
  if (technicians) target.technicians = technicians;

  if (wire.iso_arrival_time) target.arrival_time = wire.iso_arrival_time;
  if (wire.iso_departure_time) target.departure_time = wire.iso_departure_time;

  // dispatch_time normalisation: v2 wins, v1 typo fallback when only
  // the legacy field is present (CONTEXT D-11 / RESEARCH §1.6).
  const dispatch = wire.dispatch_time ?? wire.iso_dispatch_time ?? undefined;
  if (dispatch) target.dispatch_time = dispatch;

  const cancellation = adaptCancellation(wire.cancellation_details);
  if (cancellation) target.cancellation = cancellation;

  // Typed passthrough of the raw v1 block (typo intentional — CONTEXT
  // D-36 / RESEARCH §1.5). Lives in parallel with the projected
  // `cancellation` field so existing consumers keep working.
  const rawCancellation = adaptCancellationDetails(wire.cancellation_details);
  if (rawCancellation) target.cancellation_details = rawCancellation;
}

/**
 * Map one v1 appointment wire payload to the nested `Appointment` shape
 * the Vue 3 customer portal expects.
 */
export function adaptAppointment(wire: AppointmentWire): Appointment {
  const adapted: Appointment = {
    id: wire.id,
    job_id: wire.job_id,
    customer_id: wire.customer?.id,
    customer_cable_token: wire.customer?.cable_token,
    status: normaliseStatus(wire.status),
    service_category: wire.service_category
      ? { id: wire.service_category.id, name: wire.service_category.name }
      : { id: 0, name: '' },
    address: adaptAddress(wire.address),
    arrival_window_start: wire.arrival_window_start ?? '',
    arrival_window_end: wire.arrival_window_end ?? '',
    scheduled_time: wire.iso_date_time ?? '',
    photos: adaptPhotos(wire.photos),
    documents: adaptDocuments(wire.documents),
    notes: adaptNotes(wire.notes),
    settings: adaptSettings(wire.settings),
  };

  attachOptionalFields(adapted, wire);
  attachInvoiceBridgeFields(adapted, wire);

  return adapted;
}

// Surface invoice-related fields under AppointmentSummaryView's bridge keys.
// The Appointment interface doesn't declare invoice_total / invoice_balance_cents
// yet (TODO Phase 5); we attach them via index access so the existing
// `unknown as { invoice_total?: string; invoice_balance_cents?: number }` bridge
// in invoiceTotalLabel + paymentStatus reads them.
function attachInvoiceBridgeFields(target: Appointment, wire: AppointmentWire) {
  if (typeof wire.ticket?.total_real === 'string') {
    (target as { invoice_total?: string }).invoice_total =
      wire.ticket.total_real;
  }
  if (typeof wire.ticket?.balance_cents === 'number') {
    (target as { invoice_balance_cents?: number }).invoice_balance_cents =
      wire.ticket.balance_cents;
  }
}
